import { getAPI, postAPI } from "../../../../api/frameworksApi";

// Asset Coverage page (STOC): Application/Tool/Severity/Onboarded filter shared
// by the KPI cards container.

const STOC_BASE_URL = (window.BE_URL || "") + "/stocApp/api/v1";
const ASSET_COVERAGE_BASE_URL = `${STOC_BASE_URL}/asset-coverage`;

export interface AssetCoverageFilter {
  applicationNames?: string[];
  tools?: string[];
  severities?: ("Critical" | "High" | "Medium" | "Low")[];
  onboarded?: boolean;
  search?: string;
}

export interface AssetCoverageSummary {
  onboarding: { onboarded: number; notOnboarded: number; total: number };
  bySeverity: { critical: number; high: number; medium: number; low: number; totalScored: number };
}

export const fetchAssetCoverageSummary = async (
  filter: AssetCoverageFilter,
): Promise<AssetCoverageSummary> => postAPI(`${ASSET_COVERAGE_BASE_URL}/summary`, filter);

export interface AssetCoverageApplicationItem {
  applicationId?: string;
  applicationName: string;
  organization?: string;
}

export interface AssetCoverageAssetRow {
  /** Equals `ip`; pass this to the per-asset tools endpoint. */
  id: string;
  name: string;
  type: string;
  ip: string;
  environment: string;
  application?: string;
  applications?: (string | AssetCoverageApplicationItem)[] | string;
  /** Per-tool onboarding status keyed by tool, e.g. { tenableIo: "Onboarded" }. Drives dynamic table columns. */
  tools?: Record<string, string>;
  assetName?: string;
  assetScore?: number;
  toolCoverage: string[];
  toolCoverageCount: number;
  /** Tracked-tool count; constant across rows. */
  toolCoverageTotal: number;
  onboarded: boolean;
  severity: "Critical" | "High" | "Medium" | "Low";
}

export function getApplicationMappings(
  row: AssetCoverageAssetRow | (Record<string, unknown> & { application?: unknown; applications?: unknown }),
): AssetCoverageApplicationItem[] {
  const raw =
    row.applications !== undefined && row.applications !== null
      ? row.applications
      : row.application;

  if (!raw) return [];

  if (Array.isArray(raw)) {
    return (raw as unknown[])
      .map((item, idx): AssetCoverageApplicationItem | null => {
        if (!item) return null;
        if (typeof item === "string") {
          const trimmed = item.trim();
          return trimmed ? { applicationId: String(idx), applicationName: trimmed } : null;
        }
        if (typeof item === "object") {
          const obj = item as Record<string, unknown>;
          const name = (obj.applicationName || obj.name || obj.appName || "") as string;
          if (!name || !String(name).trim()) return null;
          return {
            applicationId: (obj.applicationId || obj.id || String(idx)) as string,
            applicationName: String(name).trim(),
            organization: (obj.organization || obj.orgName || obj.org || undefined) as string | undefined,
          };
        }
        return { applicationId: String(idx), applicationName: String(item) };
      })
      .filter((app): app is AssetCoverageApplicationItem => Boolean(app));
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed ? [{ applicationId: "0", applicationName: trimmed }] : [];
  }

  if (typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    const name = (obj.applicationName || obj.name || obj.appName || "") as string;
    if (name && String(name).trim()) {
      return [{
        applicationId: (obj.applicationId || obj.id || "0") as string,
        applicationName: String(name).trim(),
        organization: (obj.organization || obj.orgName || obj.org) as string | undefined,
      }];
    }
  }

  return [];
}

export interface AssetCoverageAssetsRequest extends AssetCoverageFilter {
  environments?: string[];
  types?: string[];
  /** Onboarding status filter: "Onboarded" | "Not Onboarded". */
  toolStatus?: string[];
  /** 1-based; omitted = first page. */
  page?: number;
  /** 1-100; omitted = 10. */
  pageSize?: number;
  sortKey?: "name" | "type" | "ip" | "environment" | "application" | "applications" | "assetName" | "assetScore";
  /** Tool name whose onboarding status to sort by (dynamic tool columns); used instead of sortKey. */
  sortTool?: string;
  sortDir?: "asc" | "desc";
}

export interface AssetCoverageAssetsResponse {
  total: number;
  items: AssetCoverageAssetRow[];
}

export const fetchAssetCoverageAssets = async (
  body: AssetCoverageAssetsRequest,
): Promise<AssetCoverageAssetsResponse> =>
  postAPI(`${ASSET_COVERAGE_BASE_URL}/assets/search`, body);

export interface AssetCoverageDrilldownRow {
  id: string;
  assetName: string;
  type: string;
  ip: string;
  environment: string;
  applications?: (string | AssetCoverageApplicationItem)[] | string;
}

export interface AssetCoverageDrilldownResponse {
  total: number;
  items: AssetCoverageDrilldownRow[];
}

/** Assets behind the Onboarded / Not Onboarded KPI cards. */
export const fetchAssetCoverageOnboardingAssets = async (
  status: "onboarded" | "not-onboarded",
  body: AssetCoverageAssetsRequest,
): Promise<AssetCoverageDrilldownResponse> =>
  postAPI(`${ASSET_COVERAGE_BASE_URL}/${status}-assets/search`, body);

export type AssetCoverageSeverity = "Critical" | "High" | "Medium" | "Low";

export interface AssetCoverageScoreRow extends AssetCoverageDrilldownRow {
  assetScore?: number;
  severity?: AssetCoverageSeverity;
  /** Per-tool onboarding status keyed by tool; some responses flatten these onto the row instead. */
  tools?: Record<string, string>;
  [key: string]: unknown;
}

export interface AssetCoverageScoreResponse {
  total: number;
  items: AssetCoverageScoreRow[];
}

/** Assets behind the "Assets By Asset Score" KPI cards. */
export const fetchAssetCoverageScoreAssets = async (
  body: AssetCoverageAssetsRequest & { severity: AssetCoverageSeverity },
): Promise<AssetCoverageScoreResponse> =>
  postAPI(`${ASSET_COVERAGE_BASE_URL}/asset-score/assets/search`, body);

export interface AssetCoverageToolDetail {
  tool: string;
  toolName?: string;
  onboardingStatus: "Onboarded" | "Not Onboarded";
  onboarded?: boolean;
  status?: string;
  /** yyyy-MM-dd HH:mm, UTC. Null when not onboarded, or onboarded with no heartbeat yet. */
  lastOnline: string | null;
  lastSync?: string | null;
}

/** One row per tracked tool (not just covering ones), so the modal shows gaps.
 * `assetId` is the `id` (== ip) from the search response.
 * GET /asset-coverage/assets/{ip}/tools?tools={tool name} */
export const fetchAssetCoverageAssetTools = async (
  assetId: string,
  tool: string,
): Promise<AssetCoverageToolDetail[]> => {
  const url = `${ASSET_COVERAGE_BASE_URL}/assets/${encodeURIComponent(assetId)}/tools?tools=${encodeURIComponent(tool)}`;
  const res = await getAPI(url);
  const rawList: unknown[] = Array.isArray(res)
    ? res
    : res && typeof res === "object"
      ? Array.isArray((res as Record<string, unknown>).data)
        ? ((res as Record<string, unknown>).data as unknown[])
        : Array.isArray((res as Record<string, unknown>).tools)
          ? ((res as Record<string, unknown>).tools as unknown[])
          : Array.isArray((res as Record<string, unknown>).items)
            ? ((res as Record<string, unknown>).items as unknown[])
            : Array.isArray((res as Record<string, unknown>).content)
              ? ((res as Record<string, unknown>).content as unknown[])
              : []
      : [];

  return rawList.map((item) => {
    if (!item || typeof item !== "object") {
      return { tool: String(item), onboardingStatus: "Not Onboarded", lastOnline: null };
    }
    const record = item as Record<string, unknown>;
    const tool = (record.tool || record.toolName || record.name || "") as string;
    let onboardingStatus: "Onboarded" | "Not Onboarded" = "Not Onboarded";
    if (
      record.onboardingStatus === "Onboarded" ||
      record.onboarded === true ||
      record.status === "ONBOARDED" ||
      record.status === "Onboarded"
    ) {
      onboardingStatus = "Onboarded";
    } else if (
      record.onboardingStatus === "Not Onboarded" ||
      record.onboarded === false ||
      record.status === "NOT_ONBOARDED" ||
      record.status === "Not Onboarded"
    ) {
      onboardingStatus = "Not Onboarded";
    } else if (typeof record.onboardingStatus === "string") {
      onboardingStatus = record.onboardingStatus as "Onboarded" | "Not Onboarded";
    }

    const lastOnline = (record.lastOnline || record.lastSync || record.lastSynced || record.lastSeen || null) as string | null;
    return {
      tool,
      onboardingStatus,
      lastOnline,
    };
  });
};
